<script setup lang="ts">
import { computed } from 'vue'
import { Capacitor } from '@capacitor/core'
import { ChevronLeft, ChevronRight } from '@lucide/vue'

import { RepositoryTimeline, TimelineDraft } from '@types'

import { stringToLocalDateString } from '@/helpers/dates'
import { useResponsive } from '@/plugins/responsiveUI'

import { DSButton, DSSkeleton } from '@ds'
import PeriodScrollerAxis from './PeriodScrollerAxis.vue'
import PeriodScrollerTrigger from './PeriodScrollerTrigger.vue'

type Props = {
  timeline: RepositoryTimeline
  /** Available period-end dates, oldest first — from `analyticsStore.getDatesRange`. */
  dates: string[]
  /** Currently scrubbed date — from `analyticsStore.getDate`. */
  selected?: string
  /** Settings-only bar, no axis/steppers — for views that show every period at once. */
  compact?: boolean
  disabled?: boolean
  loading?: boolean
}

type Emits = {
  'update:timeline': [timeline: RepositoryTimeline, isEndChanged?: boolean]
  'update:selected': [date: string]
}

const { timeline, dates, selected, compact = false, disabled = false, loading = false } = defineProps<Props>()
const emit = defineEmits<Emits>()

defineOptions({
  name: 'DSPeriodScroller',
})

const { isMobile } = useResponsive()
const useSheet = computed(() => isMobile.value || Capacitor.isNativePlatform())

const selectedIndex = computed(() => dates.indexOf(selected ?? ''))
const atStart = computed(() => selectedIndex.value <= 0)
const atEnd = computed(() => selectedIndex.value === -1 || selectedIndex.value >= dates.length - 1)

const selectedLabel = computed(() => stringToLocalDateString(selected ?? ''))

/**
 * With one period there is nothing to scrub, so the axis becomes a
 * read-only start–end summary. `dates` is only trustworthy once loading
 * finishes, so the axis (and its loading skeleton) stays in charge until then.
 */
const isSinglePeriod = computed(() => dates.length == 2)
const rangeStart = computed(() => stringToLocalDateString(dates.at(0)) ?? '')
const rangeEnd = computed(() => stringToLocalDateString(dates.at(-1)) ?? '')

const previousDate = computed(() => stringToLocalDateString(dates.at(selectedIndex.value - 1)))

const select = (date: string) => {
  emit('update:selected', date)
}

const step = (direction: 1 | -1) => {
  const nextIndex = Math.min(dates.length - 1, Math.max(0, selectedIndex.value + direction))
  if (nextIndex === 0) return
  const next = dates[nextIndex]
  if (next) select(next)
}

const handleApply = (draft: TimelineDraft) => {
  const isEndChanged =
    draft.end !== timeline.end || draft.end_is_today !== timeline.end_is_today
  emit('update:timeline', { ...timeline, ...draft }, isEndChanged)
}
</script>

<template>
  <div class="ds-period-scroller font-ds w-full max-sm:border max-sm:rounded-ds-lg max-sm:border-border">
    <!--
      The mobile control strip gets an edge-to-edge iris tint (design mock);
      single period gets its own inset pill instead, further down. Desktop
      stays plain since it's just the ruler + settings, no steppers.
    -->
    <div
      class="flex items-center gap-2 sm:gap-4 max-sm:bg-accent max-sm:dark:bg-body-dark max-sm:rounded-ds-lg max-sm:p-2.5"
      :class="{ 
        'max-sm:bg-primary/6 max-sm:border-primary/14 max-sm:rounded-b-none': !compact && !isSinglePeriod,
        'sm:gap-3!': isSinglePeriod
      }"
    >
      <!--
        Prev/next stepping is mobile-only: the desktop axis draws every period
        at once, so any period is a direct click away. Mobile keeps the
        steppers because the dense axis has no month labels to click precisely.
      -->
      <DSButton
        v-if="!compact && !isSinglePeriod"
        type="button"
        variant="secondary"
        size="icon"
        class="sm:hidden shrink-0 text-muted-foreground hover:bg-card hover:text-brand"
        :disabled="disabled || loading || atStart"
        aria-label="Previous period"
        @click="step(-1)"
      >
        <ChevronLeft />
      </DSButton>

      <!-- Single period, mobile: end date + start date, no arrows or ruler to scrub. -->
      <div
        v-if="isSinglePeriod"
        class="sm:hidden flex flex-col flex-1 min-w-0"
      >
        <DSSkeleton
          v-if="loading"
          class="w-16 h-4 rounded-sm bg-border shrink-0 mb-1"
        />
        <span
          v-else
          class="text-base font-bold text-foreground whitespace-nowrap"
        >
          {{ rangeEnd }}
        </span>
        <DSSkeleton
          v-if="loading"
          class="w-20 h-2.5 rounded-sm bg-border shrink-0 mb-0.5"
        />
        <span
          v-else
          class="text-xs font-medium text-muted-foreground whitespace-nowrap"
        >
          From {{ rangeStart }}
        </span>
      </div>
      <div
        v-else
        class="sm:hidden flex flex-1 min-w-0 flex-col items-center gap-0.5"
      >
        <template v-if="loading">
          <DSSkeleton class="w-20 h-2.5 rounded-sm bg-border" />
          <DSSkeleton class="w-28 h-3 rounded-lg bg-border" />
        </template>
        <template v-else>
          <span class="text-base leading-none font-bold text-foreground whitespace-nowrap">
            {{ selectedLabel }}
          </span>
          <span
            class="text-[0.625rem] font-bold text-muted-foreground whitespace-nowrap"
          >
            {{ previousDate }} to {{ selectedLabel }}
          </span>
        </template>
      </div>

      <template v-if="!compact">
        <!--
          First and last dates are always shown in full, flanking the axis —
          the axis itself only labels the periods in between (design mock).
        -->
        <div
          v-if="!isSinglePeriod"
          class="hidden sm:flex flex-1 min-w-0 items-end gap-3.5"
        >
          <template v-if="loading">
            <DSSkeleton class="w-14 h-3 mt-5 rounded-sm bg-border shrink-0" />
            <PeriodScrollerAxis
              class="flex-1 min-w-0"
              :dates
              :freq="timeline.freq"
              :selected
              :disabled
              :loading
              @select="select"
            />
            <DSSkeleton class="w-14 h-3 mt-5 rounded-sm bg-border shrink-0" />
          </template>
          <template v-else>
            <span class="flex-none text-xs font-semibold tracking-ds-tight text-foreground whitespace-nowrap">
              {{ rangeStart }}
            </span>
            <PeriodScrollerAxis
              class="flex-1 min-w-0"
              :dates
              :freq="timeline.freq"
              :selected
              :disabled
              :loading
              @select="select"
            />
            <span
              class="flex-none text-xs tracking-ds-tight whitespace-nowrap"
              :class="atEnd ? 'text-primary dark:text-brand font-bold' : 'text-foreground font-semibold'"
            >
              {{ rangeEnd }}
            </span>
          </template>
        </div>
        <!-- Single period, desktop: read-only start–end summary in place of the ruler. -->
        <div
          v-else
          class="hidden sm:flex flex-1 min-w-0 items-baseline gap-2 text-[0.875rem] font-semibold"
          :class="{ 'items-center!': loading }"
        >
          <DSSkeleton
            v-if="loading"
            class="w-16 h-4 rounded-sm bg-border shrink-0"
          />
          <span
            v-else
            class="text-foreground whitespace-nowrap"
          >{{ rangeStart }}</span>
          <span class="text-muted-foreground font-normal">to</span>
          <DSSkeleton
            v-if="loading"
            class="w-16 h-4 rounded-sm bg-border shrink-0"
          />
          <span
            v-else
            class="text-primary dark:text-brand whitespace-nowrap"
          >{{ rangeEnd }}</span>
        </div>

        <DSButton
          v-if="!isSinglePeriod"
          type="button"
          variant="secondary"
          size="icon"
          class="sm:hidden shrink-0 text-muted-foreground hover:bg-card hover:text-brand"
          :disabled="disabled || loading || atEnd"
          aria-label="Next period"
          @click="step(1)"
        >
          <ChevronRight />
        </DSButton>
        <div
          v-if="!isSinglePeriod"
          class="sm:hidden shrink-0 w-px h-6.5 bg-border"
        />
      </template>
      <slot />
      <PeriodScrollerTrigger
        :timeline
        :use-sheet="useSheet"
        :disabled="disabled || loading"
        @apply="handleApply"
      />
    </div>
    <div
      v-if="!compact && !isSinglePeriod"
      class="sm:hidden border-t border-border rounded-b-ds-lg bg-card px-4 pt-1 pb-3"
    >
      <PeriodScrollerAxis
        dense
        :dates
        :freq="timeline.freq"
        :selected
        :disabled
        :loading
        @select="select"
      />
    </div>
  </div>
</template>
